<script setup lang="ts">
import { onBeforeUnmount, onMounted } from 'vue'
import { ClipboardList, Download, House, Leaf, Moon, ScanSearch, Settings, ShieldCheck, Sun, Trophy, UserRound } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import GameIcon from '@/components/GameIcon.vue'
import AppWordmark from '@/components/AppWordmark.vue'
import SidebarPartnerCard from '@/components/SidebarPartnerCard.vue'
import RankSourceBrand from '@/components/rank/RankSourceBrand.vue'
import LanguageSelect from '@/components/LanguageSelect.vue'
import { preloadRouteView, type PrimaryRouteName } from '@/router'
import { useAppStore } from '@/stores/app'
import { pwaInstall, promptPwaInstall } from '@/services/pwa-install'
import { GAME_ICONS } from '@/ui/game-icons'

const { t } = useI18n()
const app = useAppStore()
const items: readonly { to: string; name: PrimaryRouteName; label: string; icon: typeof House; mobileSecondary?: boolean }[] = [
  { to: '/', name: 'live', label: 'nav.dashboard', icon: House },
  { to: '/calibration', name: 'calibration', label: 'nav.calibration', icon: ScanSearch },
  { to: '/history', name: 'history', label: 'nav.history', icon: ClipboardList },
  { to: '/rank', name: 'rank', label: 'nav.rank', icon: Trophy },
  { to: '/security', name: 'security', label: 'nav.security', icon: ShieldCheck, mobileSecondary: true },
  { to: '/settings', name: 'settings', label: 'nav.settings', icon: Settings },
  { to: '/about', name: 'about', label: 'nav.about', icon: UserRound, mobileSecondary: true },
]

function prepareNavigation(name: PrimaryRouteName): void {
  void preloadRouteView(name).catch(() => { /* Navigation can retry a failed prefetch. */ })
}

// Warm the frequently used settings route while idle, without mounting its UI
// or loading the deferred skin renderer. A fast click can reuse this import.
let settingsPrefetch: number | undefined
onMounted(() => {
  if (typeof window.requestIdleCallback !== 'function') return
  settingsPrefetch = window.requestIdleCallback(() => {
    settingsPrefetch = undefined
    prepareNavigation('settings')
  }, { timeout: 2000 })
})
onBeforeUnmount(() => {
  if (settingsPrefetch !== undefined) window.cancelIdleCallback(settingsPrefetch)
})

</script>

<template>
  <aside class="app-sidebar">
    <RouterLink class="brand" to="/" :aria-label="t('app.name')">
      <span class="brand-mark"><GameIcon :src="GAME_ICONS.mapleLeaf" dimension="1.875rem" eager><Leaf stroke-width="2.2" /></GameIcon></span>
      <span class="brand-copy">
        <strong class="brand-name"><AppWordmark /></strong>
        <small class="brand-subtitle">{{ t('app.tagline') }}</small>
      </span>
    </RouterLink>
    <nav class="nav-list" :aria-label="t('app.primaryNav')">
      <RouterLink v-for="item in items" :key="item.to" :to="item.to" class="nav-item" :class="[{ 'mobile-secondary': item.mobileSecondary }, `nav-item-${item.name}`]" :aria-label="item.name === 'rank' ? `${t(item.label)} · ${t('nav.rankPartner')}` : t(item.label)" @pointerenter="prepareNavigation(item.name)" @pointerdown="prepareNavigation(item.name)" @focus="prepareNavigation(item.name)">
        <span class="nav-icon"><component :is="item.icon" style="width:1.25rem;height:1.25rem" stroke-width="1.9" aria-hidden="true" /></span>
        <span class="nav-label">{{ t(item.label) }}</span>
        <span v-if="item.name === 'security'" class="nav-security-badge" aria-hidden="true">{{ t('nav.securityBadge') }}</span>
        <span v-if="item.name === 'rank'" class="nav-partner" aria-hidden="true">
          <span class="nav-partner-cross">×</span>
          <RankSourceBrand source="mxdc" />
        </span>
      </RouterLink>
    </nav>
    <div class="sidebar-bottom">
      <SidebarPartnerCard />
      <div class="sidebar-footer">
        <LanguageSelect class="sidebar-language" />
        <div class="sidebar-theme-row">
          <button class="theme-toggle" type="button" :aria-label="app.theme === 'light' ? t('app.useDark') : t('app.useLight')" @click="app.toggleTheme">
            <Moon v-if="app.theme === 'light'" style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
            <Sun v-else style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
            <span>{{ app.theme === 'light' ? t('settings.dark') : t('settings.light') }}</span>
          </button>
          <button v-if="pwaInstall.available" class="pwa-install" type="button" :aria-label="t('app.install')" @click="promptPwaInstall">
            <Download style="width:1.0625rem;height:1.0625rem" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar-bottom {
  display: flex;
  flex: 1 1 0;
  min-height: 0;
  flex-direction: column;
  justify-content: flex-end;
  gap: inherit;
}
.sidebar-bottom > .sidebar-footer { flex: none; margin-top: 0; }
@media (max-width: 47.5rem) { .sidebar-bottom { display: none; } }
@media (min-width: 47.501rem) {
  .app-sidebar { min-width: 0; transition: padding 360ms cubic-bezier(0.2, 0.8, 0.2, 1), background-color var(--motion-base) ease, border-color var(--motion-base) ease; }
  .app-sidebar > .brand, .app-sidebar > .nav-list, .sidebar-footer { flex-shrink: 0; }
}
@media (min-width: 47.501rem) and (max-height: 54rem) {
  .app-sidebar { gap: 1rem; }
  .nav-list { gap: 0.5rem; }
  .nav-item { min-height: 2.75rem; }
}

.nav-item-security,
.nav-item-security .nav-icon,
.nav-item-security .nav-security-badge {
  transition: color 0.2s ease, background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease !important;
}

.nav-item-security {
  position: relative;
  background: transparent;
  border: 0.0625rem solid transparent;
  color: var(--muted-strong);
}
.nav-item-security .nav-icon {
  color: var(--muted);
  background: transparent;
}
.nav-item-security:hover {
  background: var(--surface-subtle);
  border-color: transparent;
  color: var(--text-strong);
}
.nav-item-security:hover .nav-icon {
  color: var(--muted);
  background: transparent;
}

.nav-security-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  padding: 0.125rem 0.4375rem;
  border-radius: 999rem;
  font-size: 0.65625rem;
  font-weight: 650;
  letter-spacing: 0.02em;
  line-height: 1.2;
  background: color-mix(in srgb, var(--blue, #0284c7) 8%, var(--surface));
  color: var(--blue, #0284c7);
  border: 0.0625rem solid color-mix(in srgb, var(--blue, #0284c7) 18%, transparent);
}
@media (max-width: 70rem) {
  .nav-security-badge { display: none; }
}

.nav-item[href*="/security"].router-link-active {
  color: var(--blue, #0284c7);
  background: color-mix(in srgb, var(--blue, #0284c7) 16%, var(--surface));
  border-color: color-mix(in srgb, var(--blue, #0284c7) 36%, transparent);
  box-shadow: inset 0 0 0 0.0625rem color-mix(in srgb, var(--blue, #0284c7) 18%, transparent);
}
.nav-item[href*="/security"].router-link-active .nav-icon {
  color: var(--blue, #0284c7);
  background: color-mix(in srgb, var(--blue, #0284c7) 18%, var(--surface));
}
.nav-item[href*="/security"].router-link-active .nav-security-badge {
  background: color-mix(in srgb, var(--blue, #0284c7) 18%, var(--surface));
  color: var(--blue, #0284c7);
  border-color: color-mix(in srgb, var(--blue, #0284c7) 32%, transparent);
}

:root[data-theme='dark'] .nav-item-security {
  background: transparent;
  border-color: transparent;
  color: var(--muted-strong);
}
:root[data-theme='dark'] .nav-item-security .nav-icon {
  color: var(--muted);
  background: transparent;
}
:root[data-theme='dark'] .nav-item-security:hover {
  background: var(--surface-subtle);
  border-color: transparent;
}
:root[data-theme='dark'] .nav-item-security:hover .nav-icon {
  color: var(--muted);
  background: transparent;
}
:root[data-theme='dark'] .nav-security-badge {
  background: color-mix(in srgb, #38bdf8 10%, var(--surface));
  color: #38bdf8;
  border-color: color-mix(in srgb, #38bdf8 22%, transparent);
}
:root[data-theme='dark'] .nav-item[href*="/security"].router-link-active {
  color: #38bdf8;
  background: color-mix(in srgb, #38bdf8 18%, var(--surface));
  border-color: color-mix(in srgb, #38bdf8 40%, transparent);
  box-shadow: inset 0 0 0 0.0625rem color-mix(in srgb, #38bdf8 22%, transparent);
}
:root[data-theme='dark'] .nav-item[href*="/security"].router-link-active .nav-icon {
  color: #38bdf8;
  background: color-mix(in srgb, #38bdf8 20%, var(--surface));
}
:root[data-theme='dark'] .nav-item[href*="/security"].router-link-active .nav-security-badge {
  background: color-mix(in srgb, #38bdf8 20%, var(--surface));
  color: #38bdf8;
  border-color: color-mix(in srgb, #38bdf8 36%, transparent);
}

@media (prefers-reduced-motion: reduce) {
  .nav-item-security,
  .nav-item-security .nav-icon,
  .nav-item-security .nav-security-badge {
    transition-duration: 0.2s !important;
  }
}
</style>
